import { useCallback, useEffect, useRef } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { client } from 'modules/Apollo/client';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { WithApolloContext } from 'modules/Apollo/types';
import { logger } from 'modules/logger';
import {
  selectedHangingProtocolPayloadSelector,
  selectedHangingProtocolSmidSelector,
} from './state';
import type { HangingProtocol } from './useHangingProtocol';

// The study groups a payload is resolved against. Taken from the caller rather than derived here:
// GET_HANGING_PROTOCOL resolves viewport study/series/stack references against these, so they have
// to be the same groups the caller matched the HP against.
export type SelectedHangingProtocolQueryTarget = {
  cases: Array<Array<string>>;
} & WithApolloContext;

/**
 * What came of trying to resolve one HP smid to a case-resolved payload.
 *
 * The three outcomes used to be one `null`, and collapsing them is what let VX-4905 pass unnoticed: a
 * request the server *rejected* was indistinguishable from one that was never worth making, so the
 * caller had no way to tell "not ready yet, ask again" from "this will never work, say so".
 *
 * - `skipped` -- nothing was asked of the server. Benign and expected during load; the caller leaves
 *   the selection alone and a later render retries.
 * - `failed` -- the server was asked and did not return a protocol. Actionable: the HP the user chose
 *   is not going to be applied, and somebody has to be told.
 */
export type SelectionResolution =
  | { outcome: 'resolved'; hangingProtocol: HangingProtocol }
  | { outcome: 'skipped'; reason: 'no-case' | 'no-studies' | 'already-resolving' }
  | { outcome: 'failed'; reason: 'rejected' | 'empty' };

export type UseSelectedHangingProtocolWriters = {
  selectHangingProtocol: (smid: string) => Promise<SelectionResolution>;
  refreshSelectedHangingProtocol: () => Promise<void>;
  clearSelectedHangingProtocol: () => void;
};

/**
 * UUID-shaped runs. Study, series, case and worklist-item identifiers all take this form on the wire,
 * and `HangRequest` validation errors quote them by the dozen.
 */
const IDENTIFIER_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Cap on the logged message, so one rejection listing every study cannot dominate a log line. */
const MAX_SUMMARY_LENGTH = 500;

/**
 * The diagnosable part of a failed resolution, with identifiers stripped.
 *
 * Logging the raw error was a PHI leak rather than a theoretical one: a rejected `GET_HANGING_PROTOCOL`
 * comes back as _"Found multiple cases {…} for a set of studies […] meant to represent a single case"_
 * with every study and case id inline, and this goes to Datadog. Study and accession identifiers are
 * PHI. The shape of the message is what makes a rejection diagnosable, and it survives redaction --
 * `{<id>, <id>, <id>}` still says "three cases in one group".
 */
export function summarizeSelectionFailure(error: unknown): string {
  const raw =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : error == null
          ? '(no error)'
          : // Anything else: name the type rather than risk serialising a payload wholesale.
            `(non-error: ${typeof error})`;
  const redacted = raw.replace(IDENTIFIER_PATTERN, '<id>');
  return redacted.length > MAX_SUMMARY_LENGTH
    ? `${redacted.slice(0, MAX_SUMMARY_LENGTH)}…`
    : redacted;
}

/**
 * The context logged when a selection cannot be resolved.
 *
 * A pure function, exported, and tested -- because what this may *not* contain is the point.
 *
 * `modules/logger` forwards the context object to Datadog verbatim, and case, study and accession
 * identifiers are all PHI. The case smid used to be in here; on its own it ties a log line to a patient,
 * so it is gone, and the test asserts the payload keys against an allowlist so it cannot drift back in.
 * What remains is diagnosable without being identifying: the protocol's own smid (a user-authored
 * template, not a patient, and the right correlation key since a rejection is a property of the protocol
 * and its `cases` payload), the shape of that payload as counts, why it failed, and a redacted summary.
 */
export function buildSelectionFailureLog({
  hangingProtocolSmid,
  cases,
  reason,
  error,
}: {
  hangingProtocolSmid: string;
  cases: ReadonlyArray<ReadonlyArray<string>>;
  reason: 'rejected' | 'empty';
  error?: unknown;
}): Record<string, unknown> {
  return {
    hangingProtocolSmid,
    caseGroupCount: cases.length,
    studyIdCount: cases.reduce((total, group) => total + group.length, 0),
    reason,
    errorSummary: summarizeSelectionFailure(error),
  };
}

// One in-flight resolve per case+smid. useHangingProtocol mounts in several components at once
// (widget, layouts, viewport classifications), so without this every one of them would issue the
// same GET_HANGING_PROTOCOL on a reload.
const resolvesInFlight = new Set<string>();

/**
 * Resolves a case's HP selection to a payload, given the server-matched HP for that case.
 *
 * The selection is an smid; this decides where the matching payload comes from:
 *
 * - the server match itself, when it *is* the selected HP. This is the common case and needs no
 *   extra fetch, and it is what keeps an applied HP fresh through a refetch for free.
 * - the per-case payload cache otherwise. The two diverge when the server match sits on a different
 *   protocol than the user's choice — a criteria tie, where two HPs share study/classification
 *   criteria and the match keeps returning the older one (VX-4713).
 *
 * A cached payload for some *other* smid is ignored rather than returned: it belongs to a selection
 * that has since changed. Returning null here is what asks `useSelectedHangingProtocol` to fetch.
 */
export const resolveSelectedHangingProtocol = ({
  selectedSmid,
  cachedPayload,
  matchedHangingProtocol,
}: {
  selectedSmid: Nullable<string>;
  cachedPayload: Nullable<HangingProtocol>;
  matchedHangingProtocol: Nullable<HangingProtocol>;
}): Nullable<HangingProtocol> => {
  if (selectedSmid == null) return null;
  if (matchedHangingProtocol?.smid === selectedSmid) return matchedHangingProtocol;
  return cachedPayload?.smid === selectedSmid ? cachedPayload : null;
};

export const useSelectedHangingProtocolSmid = (): Nullable<string> => {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(selectedHangingProtocolSmidSelector(currentCaseSmid));
};

/**
 * Read-only resolution of the selection, with no fetching. Use this where the payload is only being
 * observed; `useSelectedHangingProtocol` is the variant that also fills the cache.
 */
export const useResolvedSelectedHangingProtocol = (
  matchedHangingProtocol: Nullable<HangingProtocol>
): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  const selectedSmid = useRecoilValue(selectedHangingProtocolSmidSelector(currentCaseSmid));
  const cachedPayload = useRecoilValue(selectedHangingProtocolPayloadSelector(currentCaseSmid));
  return resolveSelectedHangingProtocol({ selectedSmid, cachedPayload, matchedHangingProtocol });
};

export const useSelectedHangingProtocolWriters = ({
  cases,
  context,
}: SelectedHangingProtocolQueryTarget): UseSelectedHangingProtocolWriters => {
  const currentCaseSmid = useCurrentCaseId();

  /**
   * One queryable line per failed resolution.
   *
   * The previous call passed the raw Apollo error and nothing else, which is why VX-4905 produced no
   * actionable signal despite logging on every occurrence: no stable message to alert on, and no way to
   * tell which protocol or how big a `cases` payload provoked it. The payload itself is built by
   * `buildSelectionFailureLog`, which is where the PHI contract lives.
   */
  const logSelectionFailure = useCallback(
    ({
      smid,
      reason,
      error,
    }: {
      smid: string;
      reason: 'rejected' | 'empty';
      error?: unknown;
    }): void => {
      logger.error(
        '[HP] could not resolve the selected hanging protocol',
        buildSelectionFailureLog({ hangingProtocolSmid: smid, cases, reason, error })
      );
    },
    [cases]
  );

  // Resolves one HP smid to a payload for the current case.
  //
  // Fetches with `cases` so viewport study/series/stack references resolve against the current
  // case. A fetch WITHOUT cases returns under-resolved viewports and paints "series incompatible"
  // errors in the grid. no-cache for the reason previewing uses it: GET_HANGING_PROTOCOL resolves
  // against the current case, and writing that into the shared normalized cache would corrupt the
  // canonical (non-case-resolved) entity the HP list table reads.
  const fetchHangingProtocolForCase = useCallback(
    async (smid: string): Promise<SelectionResolution> => {
      if (currentCaseSmid == null) return { outcome: 'skipped', reason: 'no-case' };
      if (cases.length === 0) return { outcome: 'skipped', reason: 'no-studies' };
      const inFlightKey = `${currentCaseSmid}:${smid}`;
      if (resolvesInFlight.has(inFlightKey)) {
        return { outcome: 'skipped', reason: 'already-resolving' };
      }
      resolvesInFlight.add(inFlightKey);
      try {
        const { data } = await client.query({
          query: GET_HANGING_PROTOCOL,
          variables: { id: smid, cases },
          context: {
            ...context,
            telemetry: {
              ...(context.telemetry ?? {}),
              fetchPolicy: 'no-cache',
            },
          },
          fetchPolicy: 'no-cache',
        });
        const hangingProtocol = data?.hangingProtocol;
        if (hangingProtocol == null) {
          // A 200 with no protocol. Rare, and worth distinguishing from a thrown request: it means the
          // smid resolved to nothing rather than that the call could not be made.
          logSelectionFailure({ smid, reason: 'empty' });
          return { outcome: 'failed', reason: 'empty' };
        }
        return { outcome: 'resolved', hangingProtocol };
      } catch (error) {
        logSelectionFailure({ smid, reason: 'rejected', error });
        return { outcome: 'failed', reason: 'rejected' };
      } finally {
        resolvesInFlight.delete(inFlightKey);
      }
    },
    [cases, context, currentCaseSmid, logSelectionFailure]
  );

  // Records a protocol as the applied one, and primes its payload. Used where the HP the viewer should
  // show is decided by a user action rather than by the server match: confirming a browsed preview, and
  // creating a protocol (VX-4905 -- a create is an explicit choice to look at what you just built, so it
  // is applied whether or not the post-create match would have picked it).
  //
  // The smid is written only once the payload is in hand, and both in the same commit. Writing the
  // smid first opens a window where the case has a selection with nothing to show for it, and the
  // resolver falls back to the match for the duration -- which on a criteria tie is a *different*
  // protocol, so the effective HP smid changes and useHangingProtocol re-initializes the step bar
  // from the wrong protocol (the VX-4770 failure mode, reached transiently instead of permanently).
  const selectHangingProtocol = useRecoilCallback(
    ({ snapshot, set }) =>
      async (smid: string): Promise<SelectionResolution> => {
        if (currentCaseSmid == null) return { outcome: 'skipped', reason: 'no-case' };
        // What the selection was when this was asked for. Read before the await, from the snapshot the
        // callback was invoked with, because the whole point is to compare against the pre-fetch value.
        const selectionAtRequest = snapshot
          .getLoadable(selectedHangingProtocolSmidSelector(currentCaseSmid))
          .getValue();
        const resolution = await fetchHangingProtocolForCase(smid);
        // Leave the selection alone unless there is a payload to go with it, and report the outcome so
        // the caller can act. Writing the smid without the payload would be worse than writing
        // nothing: the resolver falls back to the server match for as long as the pair disagree, and on
        // a criteria tie that is a *different* protocol (the VX-4770 failure mode). So a failure stays
        // a no-op here, and becomes visible at the call site instead (VX-4905).
        if (resolution.outcome !== 'resolved') return resolution;
        // Payload first, and unconditionally. It is a cache keyed by smid, and
        // `resolveSelectedHangingProtocol` only reads it when its smid matches the selection -- so if the
        // compare-and-set below declines, this is an unread entry rather than a wrong one, and writing it
        // in the same batch is what keeps the pair from ever disagreeing when it is applied.
        set(selectedHangingProtocolPayloadSelector(currentCaseSmid), resolution.hangingProtocol);
        // **Compare-and-set: a slow pin must not overwrite a newer choice.**
        //
        // This fetch is a `getBaseViewerData`-class query, measured at 10-31s against a cold tier, and
        // the reader is free to pick a different protocol from the browse list while it is in flight.
        // Writing unconditionally meant the create's pin landed last and won: `stepped-hp.e2e` phase
        // 4h4 creates a protocol, then explicitly selects another, and the widget ended up back on the
        // created one -- reported as the browse-list choice silently reverting.
        //
        // Decided inside the updater rather than from a flag or a post-await `snapshot` read: Recoil
        // applies the updater lazily inside the batch, so a flag set in here is still unread outside,
        // and `snapshot` is the pre-await one, which cannot see a write made during the fetch. `prev` is
        // the only fresh view of the selection available here -- see this directory's CLAUDE.md,
        // "Recoil runs a `set` updater lazily".
        set(selectedHangingProtocolSmidSelector(currentCaseSmid), (prev) =>
          prev === selectionAtRequest ? smid : prev
        );
        return resolution;
      },
    [currentCaseSmid, fetchHangingProtocolForCase]
  );

  // "This copy is stale" — re-resolves the selection's payload from the server. This is what a
  // caller wants after anything that changes the HP record (an edit-form Update) or reloads the
  // case's data (Reset All).
  //
  // Reads the selected smid itself, so no caller has to capture and re-pin one; and it cannot lose
  // the user's choice, which is the whole point of the split. Overwrites rather than clearing first,
  // for the same no-gap reason described in this module's header.
  const refreshSelectedHangingProtocol = useRecoilCallback(
    ({ snapshot, set }) =>
      async (): Promise<void> => {
        if (currentCaseSmid == null) return;
        const selectedSmid = snapshot
          .getLoadable(selectedHangingProtocolSmidSelector(currentCaseSmid))
          .getValue();
        // No selection to refresh is not a failure: the case is on the server match, which needs no
        // payload of its own.
        if (selectedSmid == null) return;
        const resolution = await fetchHangingProtocolForCase(selectedSmid);
        // Overwrite rather than clearing first, for the no-gap reason in this module's header. A
        // failure leaves the previous payload in place; `fetchHangingProtocolForCase` has already
        // logged it.
        if (resolution.outcome !== 'resolved') return;
        set(selectedHangingProtocolPayloadSelector(currentCaseSmid), resolution.hangingProtocol);
      },
    [currentCaseSmid, fetchHangingProtocolForCase]
  );

  // "Forget the choice" — used when the selection itself is no longer valid, i.e. the selected HP
  // has been deleted. originalHangingProtocol then falls back to the server-matched HP.
  const clearSelectedHangingProtocol = useRecoilCallback(
    ({ reset }) =>
      () => {
        if (currentCaseSmid == null) return;
        reset(selectedHangingProtocolSmidSelector(currentCaseSmid));
        reset(selectedHangingProtocolPayloadSelector(currentCaseSmid));
      },
    [currentCaseSmid]
  );

  return {
    selectHangingProtocol,
    refreshSelectedHangingProtocol,
    clearSelectedHangingProtocol,
  };
};

/**
 * The selected HP's payload, fetching it when the cache cannot supply one.
 *
 * This is what makes the selection self-sufficient: a refresh restores only the smid, and a stale
 * payload can be dropped by whoever notices, because resolution happens here rather than at the
 * call site. No caller has to re-pin an smid to keep a selection alive through a refetch.
 */
export const useSelectedHangingProtocol = ({
  matchedHangingProtocol,
  matchedHangingProtocolLoading,
  cases,
  context,
}: {
  matchedHangingProtocol: Nullable<HangingProtocol>;
  /**
   * Whether the server match is still in flight. Load-bearing, not informational -- see the gate on
   * `needsPayload` below.
   */
  matchedHangingProtocolLoading: boolean;
} & SelectedHangingProtocolQueryTarget): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  const selectedSmid = useRecoilValue(selectedHangingProtocolSmidSelector(currentCaseSmid));
  const cachedPayload = useRecoilValue(selectedHangingProtocolPayloadSelector(currentCaseSmid));
  const { refreshSelectedHangingProtocol } = useSelectedHangingProtocolWriters({ cases, context });

  const selectedHangingProtocol = resolveSelectedHangingProtocol({
    selectedSmid,
    cachedPayload,
    matchedHangingProtocol,
  });

  // There is a selection but nothing to show for it: the case was reloaded (only the smid persists)
  // or the payload was refreshed away. Resolve it -- but only once the match has answered.
  //
  // **A selection may only displace a match that has already answered, `null` included.** The same
  // constraint the edit-time hold is documented under (VX-4872), and this is the other way to break it:
  // on a reload the smid hydrates from IndexedDB long before `getBaseViewerData` returns, so without
  // this gate the fetch fires against a match that has not spoken yet and can win the race. The viewer
  // then initializes its steps from `GET_HANGING_PROTOCOL(id, cases)` rather than from the match, and
  // `decideStepInitPass` refuses the match's version afterwards because this window has already
  // published that HP -- so the resolution the matcher picked is discarded for the rest of the session.
  //
  // Measured on a PR preview before the gate: a protocol saved with LATERAL in `Viewport-0-0-0` came
  // back after a reload rendering PA in *both* viewports, with the correct protocol applied, the
  // selection correct, the server holding LATERAL and the step reading clean -- because the render came
  // from the by-smid fetch's resolution instead of the match's. `stepped-hp-unsaved-edit-reload` and
  // `stepped-hp.e2e` phase 4e2 both failed on it.
  //
  // Gating costs nothing where the two agree, which is the common case: once the match answers with the
  // selected HP, `resolveSelectedHangingProtocol` returns it and no fetch is needed at all. The fetch is
  // only for a selection the match does *not* name, which is exactly when displacing it is legitimate.
  const needsPayload =
    selectedSmid != null && selectedHangingProtocol == null && !matchedHangingProtocolLoading;

  // Held in a ref so the effect fires on the *need* appearing rather than on the writer's identity.
  // The writer closes over `cases`, which changes as studies load, and a fetch that came back empty
  // leaves `needsPayload` true — depending on it directly would retry on every one of those renders
  // instead of once.
  const refreshRef = useRef(refreshSelectedHangingProtocol);
  refreshRef.current = refreshSelectedHangingProtocol;

  useEffect(() => {
    if (!needsPayload) return;
    void refreshRef.current();
  }, [needsPayload]);

  return selectedHangingProtocol;
};
